/**
 * TagesgeschaeftPage.jsx – Kombinierte Tagesgeschäft-Seite
 *
 * Alle Rollen: Tagesaufgaben-Checkliste (Standard + Temperatur)
 * Admin/SL: ausklappbarer "Konfigurieren"-Bereich oben (Aufgaben-Vorlagen verwalten)
 *
 * Ersetzt TagesaufgabenView.jsx + TaskTypes.js an der Hauptroute /aufgaben
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  CheckCircle2, Circle, Loader2, MapPin, Thermometer, Clock,
  ChevronRight, Settings, ChevronDown, ChevronUp, CalendarDays,
  Plus, Edit2, Trash2, ClipboardList, GripVertical,
  RefreshCw, Calendar, AlertTriangle, Camera, ImageIcon, X,
  CheckSquare, FileText, Download, Eye,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TagesgeschaeftShareBar } from '../../components/TagesgeschaeftShareBar';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const apiUrl = `${API}/api`;

/**
 * iOS-sicherer Bild-Upload (zentral für alle Stellen).
 *
 * Problem auf iOS: 
 *   - axios (XHR) hängt nach Kamera-Nutzung → App friert ein
 *   - fetch() mit File-Objekt: iOS File-Handle kann während async-Op ablaufen → leerer Body → "Field required"
 *
 * Lösung: File SOFORT als ArrayBuffer einlesen (vor jedem await), dann als Blob an fetch() übergeben.
 * Blob ist im Browser-RAM — kein iOS-File-Handle mehr nötig. fetch() setzt Content-Type korrekt.
 */
async function uploadImageiOS(url, file) {
  // 1. File sofort in RAM lesen (iOS-File-Handle-Expiry vermeiden)
  let buffer;
  if (typeof file.arrayBuffer === 'function') {
    buffer = await file.arrayBuffer();
  } else {
    // Fallback für ältere iOS-Versionen ohne arrayBuffer()
    buffer = await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload  = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsArrayBuffer(file);
    });
  }

  // 2. Blob aus ArrayBuffer — immer im Speicher, kein Handle-Problem
  const blob = new Blob([buffer], { type: file.type || 'image/jpeg' });

  // 3. FormData mit Blob (nicht File) — Browser setzt Content-Type: multipart/form-data; boundary=...
  const fd = new FormData();
  fd.append('file', blob, file.name || 'image.jpg');

  // 4. fetch() — kein XHR/axios (kein iOS-XHR-Hang-Bug nach Kamera)
  //    Authorization-Header als echtes Headers-Objekt (nicht Plain-Object) → kein Conflict mit Auto-Content-Type
  const h = new Headers();
  h.set('Authorization', `Bearer ${localStorage.getItem('token')}`);

  const resp = await fetch(url, { method: 'POST', headers: h, body: fd });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const d = err.detail;
    throw new Error(Array.isArray(d) ? d.map(x => x.msg).join('; ') : (d || `HTTP ${resp.status}`));
  }
  return resp.json();
}

// ── Konstanten (aus TaskTypes.js) ─────────────────────────────────────────────
const TASK_TYPE_OPTIONS = [
  { value: 'standard',   label: 'Standard',   icon: CheckSquare, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' },
  { value: 'temperatur', label: 'Temperatur', icon: Thermometer, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30' },
];
const PRIORITY_OPTIONS = [
  { value: 'niedrig', label: 'Niedrig', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'mittel',  label: 'Mittel',  cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'hoch',    label: 'Hoch',    cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  { value: 'kritisch',label: 'Kritisch',cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
];
const TURNUS_OPTIONS = [
  { value: 'täglich',     label: 'Täglich',     icon: RefreshCw },
  { value: 'wöchentlich', label: 'Wöchentlich', icon: Calendar },
  { value: 'monatlich',   label: 'Monatlich',   icon: Calendar },
  { value: 'einmalig',    label: 'Einmalig',    icon: Clock },
];
const WOCHENTAGE = [
  { value: 'montag', label: 'Mo' }, { value: 'dienstag', label: 'Di' },
  { value: 'mittwoch', label: 'Mi' }, { value: 'donnerstag', label: 'Do' },
  { value: 'freitag', label: 'Fr' }, { value: 'samstag', label: 'Sa' },
  { value: 'sonntag', label: 'So' },
];
const emptyForm = () => ({
  name: '', description: '', priority: 'mittel',
  task_type: 'standard', is_arbeitsbeginn: false,
  location_ids: [], shift_ids: [],
  turnus: { typ: 'täglich', wochentage: [], monatstag: 1, einmalig_datum: '' },
  deadline_time: '', ab_uhrzeit: '', ziel_uhrzeit: '', photo_required: false,
  photo_required_count: 0,
  is_closing_task: false, is_opening_task: false, blocks_others: false,
  reference_image_url: null,
});

const _isTurnusDue = (turnus, today) => {
  if (!turnus?.typ) return true;
  if (turnus.typ === 'täglich') return true;
  if (turnus.typ === 'wöchentlich') {
    const dayMap = ['sonntag','montag','dienstag','mittwoch','donnerstag','freitag','samstag'];
    const dayName = dayMap[today.getDay()];
    return (turnus.wochentage || []).includes(dayName);
  }
  if (turnus.typ === 'monatlich') return today.getDate() === (turnus.monatstag || 1);
  if (turnus.typ === 'einmalig') return turnus.einmalig_datum === today.toISOString().slice(0,10);
  return true;
};

// ── LocationSelector ──────────────────────────────────────────────────────────
function LocationSelector({ locations, value, onChange }) {
  if (!locations.length) return null;
  if (locations.length === 1) return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <MapPin className="h-3.5 w-3.5 text-primary" />
      <span className="font-medium text-foreground">{locations.find(l => l.id === value)?.name}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
        data-testid="location-selector">
        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

// ── Tagesaufgaben-Karte ───────────────────────────────────────────────────────
function TaskCheckCard({ task, onComplete, onUndo, apiUrl, authH, isAdmin }) {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);   // URL des vergrößerten Fotos
  const photoInputRef = useRef();
  const done = task.is_completed;
  const required = task.photo_required_count || 0;
  const hasEnoughPhotos = required === 0 || photos.length >= required;
  const completionPhotos = task.completion_photos || [];

  const uploadOnePhoto = async (file) => {
    setUploading(true);
    try {
      const data = await uploadImageiOS(
        `${apiUrl}/tasks/completion-photo-upload?template_id=${task.id}`,
        file
      );
      setPhotos(prev => [...prev, { url: data.photo_url, name: file.name }]);
    } catch (e) {
      toast.error(e.message || 'Foto-Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const handleComplete = async () => {
    if (!hasEnoughPhotos) {
      toast.error(`Bitte erst ${required} Foto${required > 1 ? 's' : ''} aufnehmen`);
      return;
    }
    setLoading(true);
    try {
      await onComplete(task.id, photos.map(p => p.url));
      setPhotos([]);
    } finally { setLoading(false); }
  };

  const completedAt = task.completed_at
    ? new Date(task.completed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <>
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-all ${
      done ? 'opacity-70 bg-muted/30 border-border' :
      task.is_arbeitsbeginn ? 'border-orange-400 bg-orange-50/60 dark:bg-orange-900/10' :
      task.is_overdue ? 'border-red-300 bg-red-50/30 dark:bg-red-900/10' : 'border-border bg-card'
    }`} data-testid={`task-card-${task.id}`}>
      <button onClick={done ? async () => { setLoading(true); try { await onUndo(task.id); } finally { setLoading(false); } } : handleComplete}
        disabled={loading || uploading}
        className={`mt-0.5 shrink-0 transition-opacity ${!done && !hasEnoughPhotos ? 'text-muted-foreground cursor-not-allowed opacity-40' : 'text-primary hover:opacity-75'}`}
        data-testid={`task-toggle-${task.id}`}>
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> :
          done ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <Circle className="h-5 w-5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {task.is_arbeitsbeginn && !done && (
            <span className="text-[10px] bg-orange-100 text-orange-700 dark:bg-orange-900/30 px-1.5 py-0.5 rounded font-semibold">Arbeitsbeginn</span>
          )}
          <p className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>{task.name}</p>
        </div>
        {task.description && <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>}
        {/* Uhrzeiten */}
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {task.ab_uhrzeit && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" /> ab {task.ab_uhrzeit} Uhr
            </span>
          )}
          {task.ziel_uhrzeit && (
            <span className={`flex items-center gap-1 text-xs ${task.is_overdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
              <Clock className="h-3 w-3" /> bis {task.ziel_uhrzeit} Uhr
            </span>
          )}
          {done && task.completed_by_name && (
            <span className="text-xs text-muted-foreground">✓ {task.completed_by_name}{completedAt ? ` · ${completedAt}` : ''}</span>
          )}
        </div>
        {/* Info-Anhänge */}
        {(task.reference_image_url || task.attachment_pdf_url) && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {task.reference_image_url && (
              <button onClick={() => setLightbox(task.reference_image_url)}
                className="mt-1 block rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary transition-all w-12 h-12 shrink-0"
                data-testid="task-image-link" title="Bild ansehen">
                <img src={task.reference_image_url} alt="Referenz"
                  className="w-full h-full object-cover"
                  onError={e => { e.target.style.display='none'; e.currentTarget.parentElement.style.display='none'; }} />
              </button>
            )}
            {task.attachment_pdf_url && task.attachment_pdf_url !== 'pending' && (
              <a href={task.attachment_pdf_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid="task-pdf-link">
                <FileText className="h-3 w-3" /> PDF öffnen
              </a>
            )}
          </div>
        )}
        {/* Pflichtfoto-Upload (nur wenn nicht erledigt) */}
        {!done && required > 0 && (
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <Camera className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="text-xs font-medium text-primary">
                {photos.length}/{required} Pflichtfoto{required > 1 ? 's' : ''} aufgenommen
              </span>
              {photos.length < required && (
                <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploading}
                  className="ml-auto flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-lg hover:opacity-90 disabled:opacity-50"
                  data-testid={`task-photo-btn-${task.id}`}>
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Foto hinzufügen
                </button>
              )}
            </div>
            {photos.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p.url} alt={`Foto ${i+1}`} className="h-14 w-14 object-cover rounded-lg border"
                      onError={e => { e.target.style.display='none'; }} />
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadOnePhoto(f); e.target.value = ''; }} />
          </div>
        )}
        {/* Admin/SL: Nachweis-Fotos der erledigten Aufgabe */}
        {isAdmin && done && completionPhotos.length > 0 && (
          <div className="mt-2 space-y-1" data-testid={`task-completion-photos-${task.id}`}>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Nachweis-Fotos ({completionPhotos.length})
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {completionPhotos.map((url, i) => (
                <button key={i} type="button" onClick={() => setLightbox(url)}
                  className="relative group overflow-hidden rounded-lg border hover:ring-2 hover:ring-primary transition-all"
                  data-testid={`completion-photo-thumb-${i}`}>
                  <img src={url} alt={`Nachweis ${i+1}`}
                    className="h-16 w-16 object-cover"
                    onError={e => { e.target.src=''; e.target.style.background='#f3f4f6'; }} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Eye className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Admin/SL: Hinweis wenn Pflichtfotos gefordert, aber keine vorhanden (Altdaten) */}
        {isAdmin && done && required > 0 && completionPhotos.length === 0 && (
          <p className="text-[10px] text-amber-600 mt-1">⚠ Keine Nachweisfotos vorhanden</p>
        )}
      </div>
    </div>
    {/* Lightbox */}
    {lightbox && (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={() => setLightbox(null)} data-testid="photo-lightbox">
        <img src={lightbox} alt="Nachweis" className="max-h-[85vh] max-w-full rounded-xl shadow-2xl object-contain" />
        <button onClick={() => setLightbox(null)}
          className="absolute top-4 right-4 bg-white/20 text-white rounded-full p-2 hover:bg-white/30">
          <X className="h-5 w-5" />
        </button>
      </div>
    )}
    </>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function TagesgeschaeftPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isConfigurator = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  // ── Gemeinsamer State ─────────────────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');

  // ── Checklisten-State ─────────────────────────────────────────────────────
  const [checkData, setCheckData] = useState(null);
  const [checkLoading, setCheckLoading] = useState(true);

  // ── Konfig-State (Admin/SL) ───────────────────────────────────────────────
  const [showKonfig, setShowKonfig] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [konfigLoading, setKonfigLoading] = useState(false);
  const [filterLocation, setFilterLocation] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const imageFileRef = useRef();
  const pdfFileRef   = useRef();
  const [imagePreview, setImagePreview]           = useState(null);
  const [attachmentPdfUrl, setAttachmentPdfUrl]   = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingPdfFile, setPendingPdfFile]     = useState(null);
  const [uploadDebug, setUploadDebug]           = useState(null); // temporäres iOS-Debug-Panel
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  // ── Standorte laden ───────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${apiUrl}/user/locations`, { headers: authH() }).then(r => {
      const locs = r.data || [];
      setLocations(locs);
      if (locs.length > 0) {
        const owned = user?.location_ids?.length ? locs.find(l => user.location_ids.includes(l.id)) : null;
        setSelectedLocId(owned?.id || locs[0].id);
      }
    }).catch(() => toast.error('Standorte konnten nicht geladen werden'));
  }, [user?.id]);

  // ── Checkliste laden ──────────────────────────────────────────────────────
  const fetchCheck = useCallback(async (opts = {}) => {
    if (!selectedLocId) return;
    if (!opts.silent) setCheckLoading(true);
    try {
      const r = await axios.get(`${apiUrl}/tasks/tagesaufgaben?location_id=${selectedLocId}`, { headers: authH() });
      setCheckData(r.data);
    } catch { toast.error('Fehler beim Laden der Aufgaben'); }
    finally { if (!opts.silent) setCheckLoading(false); }
  }, [selectedLocId]);
  useEffect(() => { fetchCheck(); }, [fetchCheck]);

  // ── Konfig laden (Admin/SL) ───────────────────────────────────────────────
  const fetchKonfig = useCallback(async () => {
    if (!isConfigurator) return;
    setKonfigLoading(true);
    try {
      const r = await axios.get(`${apiUrl}/tasks/templates`, { headers: authH() });
      const raw = r.data || [];
      raw.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setTasks(raw);
    } catch { /* silent */ }
    finally { setKonfigLoading(false); }
  }, [isConfigurator]);
  useEffect(() => { if (isConfigurator) fetchKonfig(); }, [fetchKonfig]);

  // ── Checklisten-Handler ───────────────────────────────────────────────────
  const completeTask = async (templateId, photoUrls = []) => {
    await axios.post(`${apiUrl}/tasks/completions`,
      { template_id: templateId, location_id: selectedLocId, completion_photos: photoUrls },
      { headers: authH() }
    );
    fetchCheck({ silent: true }); toast.success('Aufgabe erledigt!');
  };
  const undoTask = async (templateId) => {
    await axios.delete(`${apiUrl}/tasks/completions/${templateId}?location_id=${selectedLocId}`, { headers: authH() });
    fetchCheck({ silent: true }); toast.info('Erledigung zurückgenommen');
  };

  // ── Konfig-Handler ────────────────────────────────────────────────────────
  const getFilteredTasks = () => {
    const list = filterLocation === 'all' ? tasks : tasks.filter(t => (t.location_ids || []).includes(filterLocation));
    return [...list].sort((a, b) => {
      const az = a.ab_uhrzeit || a.ziel_uhrzeit || '99:99';
      const bz = b.ab_uhrzeit || b.ziel_uhrzeit || '99:99';
      return az.localeCompare(bz);
    });
  };
  const getTypeInfo = (type) => TASK_TYPE_OPTIONS.find(o => o.value === type) || TASK_TYPE_OPTIONS[0];

  const openCreate = () => {
    setEditId(null); setForm(emptyForm()); setImagePreview(null);
    setAttachmentPdfUrl(null); setPendingImageFile(null); setPendingPdfFile(null);
    setModalOpen(true);
  };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      name: t.name || '', description: t.description || '', priority: t.priority || 'mittel',
      task_type: t.task_type || 'standard', is_arbeitsbeginn: t.is_arbeitsbeginn || false,
      location_ids: t.location_ids || [], shift_ids: t.shift_ids || [],
      turnus: t.turnus || { typ: 'täglich', wochentage: [], monatstag: 1, einmalig_datum: '' },
      deadline_time: t.deadline_time || '', ab_uhrzeit: t.ab_uhrzeit || '',
      ziel_uhrzeit: t.ziel_uhrzeit || '', photo_required: t.photo_required || false,
      is_closing_task: t.is_closing_task || false, is_opening_task: t.is_opening_task || false,
      blocks_others: t.blocks_others || false, reference_image_url: t.reference_image_url || null,
      photo_required_count: t.photo_required_count || 0,
    });
    setImagePreview(t.reference_image_url || null);
    setAttachmentPdfUrl(t.attachment_pdf_url || null);
    setPendingImageFile(null);
    setPendingPdfFile(null);
    setModalOpen(true);
  };

  // uploadTaskImage nutzt jetzt den zentralen uploadImageiOS-Helper
  const uploadTaskImage = async (taskId, file) => {
    setUploadDebug(`Starte Upload\nname=${file.name}\nsize=${file.size}\ntype=${file.type || '(leer)'}\n→ ArrayBuffer lesen...`);
    try {
      const data = await uploadImageiOS(
        `${apiUrl}/tasks/templates/${taskId}/upload-image`,
        file
      );
      setUploadDebug(prev => `${prev}\nHTTP:200 ✅`);
      return data;
    } catch (e) {
      setUploadDebug(prev => `${prev}\nFEHLER: ${e.message}`);
      throw e;
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht'); return; }
    if (!form.location_ids.length) { toast.error('Mindestens ein Standort wählen'); return; }
    if (form.turnus.typ === 'wöchentlich' && !form.turnus.wochentage.length) { toast.error('Wochentage wählen'); return; }
    setSaving(true);

    const extractDetail = (e) => {
      if (e instanceof Error && e.message) return e.message;
      const d = e?.response?.data?.detail;
      if (typeof d === 'string' && d) return d;
      if (Array.isArray(d) && d.length) return d.map(x => x.msg || JSON.stringify(x)).join('; ');
      return e?.message || 'Fehler';
    };

    try {
      // 1. Bild ZUERST hochladen → keine blob-URL in DB
      let imageUrlToSave = imagePreview && !imagePreview.startsWith('blob:') ? imagePreview : null;
      if (pendingImageFile) {
        const taskId = editId || null;
        if (taskId) {
          const upData = await uploadTaskImage(taskId, pendingImageFile);
          imageUrlToSave = upData.reference_image_url;
          setPendingImageFile(null);
        }
      }

      // 2. Template anlegen / aktualisieren
      const payload = { ...form, reference_image_url: imageUrlToSave };
      let res;
      if (editId) res = await axios.put(`${apiUrl}/tasks/templates/${editId}`, payload, { headers: authH() });
      else        res = await axios.post(`${apiUrl}/tasks/templates`, payload, { headers: authH() });
      const savedId = editId || res.data?.id;

      // 3. Bild für NEUE Aufgabe nachträglich hochladen (erst nach Anlage bekannt welche ID)
      if (savedId && !editId && pendingImageFile) {
        try {
          const upData = await uploadTaskImage(savedId, pendingImageFile);
          // Bild-URL nachführen
          await axios.put(`${apiUrl}/tasks/templates/${savedId}`,
            { reference_image_url: upData.reference_image_url },
            { headers: authH() }
          );
        } catch (upErr) {
          toast.error(`Aufgabe angelegt, aber Bild-Upload fehlgeschlagen: ${upErr.message}`);
        }
        setPendingImageFile(null);
      }

      // 4. PDF hochladen
      if (savedId && pendingPdfFile) {
        const fd = new FormData(); fd.append('file', pendingPdfFile, pendingPdfFile.name || 'anhang.pdf');
        await axios.post(`${apiUrl}/tasks/templates/${savedId}/upload-pdf`, fd, { headers: authH() });
        setPendingPdfFile(null);
      }

      toast.success(editId ? 'Aufgabe gespeichert' : 'Aufgabe angelegt');
      setModalOpen(false); fetchKonfig(); fetchCheck();
    } catch (e) {
      toast.error(extractDetail(e));
      console.error('[TaskSave]', e);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return;
    // Sofortiges lokales State-Update — kein Reload nötig
    setTasks(prev => prev.filter(t => t.id !== id));
    setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    try {
      await axios.delete(`${apiUrl}/tasks/templates/${id}`, { headers: authH() });
      fetchCheck();
    } catch (e) {
      // Bei Fehler: State zurücksetzen
      fetchKonfig();
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} Aufgabe${ids.length > 1 ? 'n' : ''} wirklich löschen?`)) return;
    setBulkDeleting(true);
    // Sofortiges lokales State-Update
    setTasks(prev => prev.filter(t => !selectedIds.has(t.id)));
    setSelectedIds(new Set());
    try {
      await axios.delete(`${apiUrl}/tasks/templates-bulk`, {
        headers: authH(),
        data: { ids },
      });
      fetchCheck();
      toast.success(`${ids.length} Aufgabe${ids.length > 1 ? 'n' : ''} gelöscht`);
    } catch (e) {
      fetchKonfig();
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => {
    const filtered = getFilteredTasks();
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(t => t.id)));
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const from = dragIdx.current; const to = dragOverIdx.current;
    if (from === null || to === null || from === to) { dragIdx.current = null; dragOverIdx.current = null; return; }
    dragIdx.current = null; dragOverIdx.current = null;
    const filtered = getFilteredTasks();
    const reordered = [...filtered];
    const [moved] = reordered.splice(from, 1); reordered.splice(to, 0, moved);
    const updatedAll = tasks.map(t => { const idx = reordered.findIndex(r => r.id === t.id); return idx >= 0 ? { ...t, sort_order: idx } : t; });
    setTasks(updatedAll);
    try { await axios.put(`${apiUrl}/tasks/templates/reorder`, reordered.map((t, i) => ({ id: t.id, sort_order: i })), { headers: authH() }); }
    catch { /* behalte UI */ }
  };

  const toggleWochentag = (val) => setForm(f => ({
    ...f, turnus: { ...f.turnus, wochentage: f.turnus.wochentage.includes(val) ? f.turnus.wochentage.filter(w => w !== val) : [...f.turnus.wochentage, val] }
  }));
  const toggleLocation = (id) => setForm(f => ({
    ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter(x => x !== id) : [...f.location_ids, id]
  }));

  const getTurnusLabel = (turnus) => {
    if (!turnus?.typ) return 'Täglich';
    if (turnus.typ === 'wöchentlich') return turnus.wochentage?.length ? `Wöchentlich (${turnus.wochentage.map(w => w.slice(0,2)).join(', ')})` : 'Wöchentlich';
    if (turnus.typ === 'monatlich') return `Monatlich (${turnus.monatstag}.)`; 
    if (turnus.typ === 'einmalig') return `Einmalig (${turnus.einmalig_datum || '–'})`;
    return 'Täglich';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const today = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const openTaskCount = checkData?.standard_tasks?.filter(t => !t.is_completed).length || 0;
  const doneTaskCount = checkData?.standard_tasks?.filter(t => t.is_completed).length || 0;
  const filteredTasks = getFilteredTasks();

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 pb-8 space-y-4 pt-2" data-testid="tagesgeschaeft-page">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold">Tagesgeschäft</h1>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <CalendarDays className="h-3.5 w-3.5" />{today}
            </p>
          </div>
          <LocationSelector locations={locations} value={selectedLocId} onChange={setSelectedLocId} />
        </div>

        {/* ── Admin/SL: Konfigurieren-Bereich ────────────────────────────── */}
        {isConfigurator && (
          <div className="border border-border rounded-xl overflow-hidden" data-testid="konfig-bereich">
            <button onClick={() => setShowKonfig(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
              data-testid="konfig-toggle">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Konfigurieren (Admin/Stationsleitung)</span>
                {tasks.length > 0 && <span className="text-xs text-muted-foreground">· {tasks.length} Aufgaben</span>}
              </div>
              {showKonfig ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>

            {showKonfig && (
              <div className="p-4 border-t border-border space-y-4">
                {['admin', 'stationsleitung', 'superadmin'].includes(user?.role) && (
                  <TagesgeschaeftShareBar locationId={filterLocation !== 'all' ? filterLocation : selectedLocId} onSent={fetchKonfig} />
                )}
                {/* Filter + Neue Aufgabe */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {locations.length > 1 && (
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant={filterLocation === 'all' ? 'default' : 'outline'} className="cursor-pointer px-3 py-1" onClick={() => setFilterLocation('all')}>Alle Standorte</Badge>
                      {locations.map(l => <Badge key={l.id} variant={filterLocation === l.id ? 'default' : 'outline'} className="cursor-pointer px-3 py-1" onClick={() => setFilterLocation(l.id)}>{l.name}</Badge>)}
                    </div>
                  )}
                  <div className="flex gap-2 items-center shrink-0">
                    {selectedIds.size > 0 && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                        className="gap-1.5"
                        data-testid="bulk-delete-btn"
                      >
                        {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        Ausgewählte löschen ({selectedIds.size})
                      </Button>
                    )}
                    <Button onClick={openCreate} size="sm" className="gap-1.5" data-testid="create-task-btn">
                      <Plus className="h-4 w-4" /> Neue Aufgabe
                    </Button>
                  </div>
                </div>

                {/* Task-Liste */}
                {konfigLoading ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : filteredTasks.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">Noch keine Aufgaben angelegt</div>
                ) : (
                  <div className="space-y-1.5" onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
                    {/* Alle auswählen */}
                    <div className="flex items-center gap-2 px-1 pb-1">
                      <input
                        type="checkbox"
                        checked={filteredTasks.length > 0 && selectedIds.size === filteredTasks.length}
                        onChange={toggleSelectAll}
                        className="rounded"
                        data-testid="select-all-checkbox"
                      />
                      <span className="text-xs text-muted-foreground">Alle auswählen</span>
                    </div>
                    {filteredTasks.map((task, index) => {
                      const priority = PRIORITY_OPTIONS.find(p => p.value === task.priority);
                      const typeInfo = getTypeInfo(task.task_type);
                      const TypeIcon = typeInfo.icon;
                      const isSelected = selectedIds.has(task.id);
                      return (
                        <div key={task.id} draggable
                          onDragStart={() => { dragIdx.current = index; }}
                          onDragOver={e => { e.preventDefault(); dragOverIdx.current = index; }}
                          className={`rounded-xl border transition-colors px-3 py-2.5 ${
                            isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/20'
                          }`}
                          data-testid={`konfig-task-${task.id}`}>

                          {/* Zeile 1: Checkbox + Grip + Name (volle Breite, kein truncate) */}
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(task.id)}
                              onClick={e => e.stopPropagation()}
                              className="rounded shrink-0 mt-0.5"
                              data-testid={`task-checkbox-${task.id}`}
                            />
                            <GripVertical className="h-4 w-4 text-muted-foreground/30 cursor-grab shrink-0 mt-0.5" />
                            <span className="font-medium text-sm flex-1 break-words min-w-0">{task.name}</span>
                          </div>

                          {/* Zeile 2: Badges + Aktions-Buttons */}
                          <div className="flex items-center gap-1.5 mt-1.5 pl-10 flex-wrap">
                            <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${typeInfo.color}`}>
                              <TypeIcon className="h-3 w-3" />{typeInfo.label}
                            </span>
                            {priority && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0 ${priority.cls}`}>{priority.label}</span>
                            )}
                            {task.is_arbeitsbeginn && (
                              <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold shrink-0">AB</span>
                            )}
                            {task.ab_uhrzeit && (
                              <span className="text-[10px] text-muted-foreground shrink-0">{task.ab_uhrzeit} Uhr</span>
                            )}
                            <div className="ml-auto flex items-center gap-0.5">
                              <button onClick={() => openEdit(task)} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/60 transition-colors" data-testid={`edit-task-${task.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
                              <button onClick={() => handleDelete(task.id)} className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-muted/60 transition-colors" data-testid={`delete-task-${task.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Tagesaufgaben-Checkliste (alle Rollen) ─────────────────────── */}
        <div data-testid="tagesaufgaben-checkliste">
          {checkLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {(checkData?.standard_tasks?.length || 0) > 0 && (
                <div className="flex items-center gap-3 bg-muted/30 rounded-xl px-4 py-3 mb-3">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${Math.round(doneTaskCount / (openTaskCount + doneTaskCount) * 100) || 0}%` }} />
                  </div>
                  <span className="text-sm font-medium shrink-0">{doneTaskCount}/{openTaskCount + doneTaskCount} erledigt</span>
                </div>
              )}
              <div className="space-y-2">
                {([...(checkData?.standard_tasks || [])].sort((a, b) => {
                    const az = a.ab_uhrzeit || '99:99';
                    const bz = b.ab_uhrzeit || '99:99';
                    return az.localeCompare(bz);
                  })).map(task => (
                  <TaskCheckCard key={task.id} task={task} onComplete={completeTask} onUndo={undoTask} apiUrl={apiUrl} authH={authH} isAdmin={['admin', 'stationsleitung'].includes(user?.role)} />
                ))}
              </div>
              {checkData?.has_temperatur && (
                <button onClick={() => navigate('/admin/temperature-input')}
                  className={`mt-2 w-full flex items-center justify-between rounded-xl border px-4 py-3 transition-colors ${
                    checkData.temperatur_done ? 'border-green-300 bg-green-50/60 dark:bg-green-900/10' : 'border-sky-300 bg-sky-50/60 dark:bg-sky-900/10 hover:bg-sky-100/60'
                  }`} data-testid="temperatur-card">
                  <div className="flex items-center gap-3">
                    {checkData.temperatur_done ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <Thermometer className="h-5 w-5 text-sky-500" />}
                    <div className="text-left">
                      <p className="text-sm font-semibold">Temperaturkontrolle</p>
                      <p className="text-xs text-muted-foreground">{checkData.temperatur_done ? '✅ Heute erledigt' : 'Zur Temperaturkontrolle'}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              {!checkData?.standard_tasks?.length && !checkData?.has_temperatur && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-medium">Keine Aufgaben für heute</p>
                  {isConfigurator && <p className="text-xs mt-1">Aufgaben im Konfigurieren-Bereich anlegen</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Aufgabe-Formular-Modal (Admin/SL) ────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-modal">
          <DialogHeader>
            <DialogTitle>{editId ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Aufgaben-Typ */}
            <div className="space-y-1.5">
              <Label>Aufgaben-Typ *</Label>
              <div className="flex flex-wrap gap-2">
                {TASK_TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button key={opt.value} type="button"
                      onClick={() => setForm(f => ({ ...f, task_type: opt.value }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${form.task_type === opt.value ? `${opt.color} border-current` : 'border-border text-muted-foreground hover:border-primary/40'}`}
                      data-testid={`task-type-${opt.value}`}>
                      <Icon className="h-3.5 w-3.5" />{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Name */}
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z.B. Kühlschrank kontrollieren" data-testid="task-name-input" />
            </div>
            {/* Arbeitsbeginn (nur Standard) */}
            {form.task_type === 'standard' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_arbeitsbeginn} onChange={e => setForm(f => ({ ...f, is_arbeitsbeginn: e.target.checked }))} className="rounded" data-testid="is-arbeitsbeginn-checkbox" />
                <span className="text-sm">Bei Arbeitsbeginn erledigen <span className="text-xs text-muted-foreground">(oben hervorgehoben)</span></span>
              </label>
            )}
            {/* Beschreibung */}
            <div className="space-y-1.5">
              <Label>Beschreibung <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Detailierte Anleitung…" />
            </div>
            {/* Priorität */}
            <div className="space-y-1.5">
              <Label>Priorität</Label>
              <div className="flex flex-wrap gap-2">
                {PRIORITY_OPTIONS.map(p => (
                  <button key={p.value} type="button" onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${form.priority === p.value ? `${p.cls} border-current` : 'border-border text-muted-foreground'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Standorte */}
            <div className="space-y-1.5">
              <Label>Standort(e) * <span className="text-muted-foreground font-normal">(Mehrfachauswahl)</span></Label>
              <div className="flex flex-wrap gap-2">
                {locations.map(l => (
                  <button key={l.id} type="button" onClick={() => toggleLocation(l.id)}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${form.location_ids.includes(l.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            {/* Turnus */}
            <div className="space-y-2">
              <Label>Turnus (Wiederholung) *</Label>
              <Select value={form.turnus.typ} onValueChange={v => setForm(f => ({ ...f, turnus: { ...f.turnus, typ: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TURNUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.turnus.typ === 'wöchentlich' && (
                <div className="flex flex-wrap gap-1.5">
                  {WOCHENTAGE.map(w => (
                    <button key={w.value} type="button" onClick={() => toggleWochentag(w.value)}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${form.turnus.wochentage.includes(w.value) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>
                      {w.label}
                    </button>
                  ))}
                </div>
              )}
              {form.turnus.typ === 'monatlich' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">Am</span>
                  <Input type="number" min="1" max="31" value={form.turnus.monatstag || 1} onChange={e => setForm(f => ({ ...f, turnus: { ...f.turnus, monatstag: parseInt(e.target.value) } }))} className="w-20 h-8" />
                  <span className="text-sm">. des Monats</span>
                </div>
              )}
              {form.turnus.typ === 'einmalig' && (
                <Input type="date" value={form.turnus.einmalig_datum || ''} onChange={e => setForm(f => ({ ...f, turnus: { ...f.turnus, einmalig_datum: e.target.value } }))} className="h-9" />
              )}
            </div>
            {/* Zeitfenster */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Ab Uhrzeit <span className="text-muted-foreground font-normal text-xs">(Richtzeit)</span></Label>
                <div className="flex items-center gap-1">
                  <Input type="time" value={form.ab_uhrzeit} onChange={e => setForm(f => ({ ...f, ab_uhrzeit: e.target.value }))} className="flex-1 h-9" />
                  {form.ab_uhrzeit && <button onClick={() => setForm(f => ({ ...f, ab_uhrzeit: '' }))} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Ziel-Uhrzeit <span className="text-muted-foreground font-normal text-xs">(Frist)</span></Label>
                <div className="flex items-center gap-1">
                  <Input type="time" value={form.ziel_uhrzeit} onChange={e => setForm(f => ({ ...f, ziel_uhrzeit: e.target.value }))} className="flex-1 h-9" />
                  {form.ziel_uhrzeit && <button onClick={() => setForm(f => ({ ...f, ziel_uhrzeit: '' }))} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
            </div>
            {/* Bild-Pflicht bei Erledigung */}
            <div className="space-y-2 border border-primary/20 rounded-xl p-3 bg-primary/5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={form.photo_required_count > 0}
                  onChange={e => setForm(f => ({ ...f, photo_required_count: e.target.checked ? 1 : 0 }))}
                  className="rounded accent-primary"
                  data-testid="photo-required-toggle"
                />
                <span className="text-sm font-medium">Bild-Pflicht bei Erledigung</span>
              </label>
              {form.photo_required_count > 0 && (
                <div className="flex items-center gap-2 ml-5">
                  <span className="text-xs text-muted-foreground">Anzahl Pflichtfotos:</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setForm(f => ({ ...f, photo_required_count: Math.max(1, (f.photo_required_count||1)-1) }))}
                      className="h-6 w-6 rounded border flex items-center justify-center text-muted-foreground hover:bg-muted">–</button>
                    <span className="w-8 text-center text-sm font-bold">{form.photo_required_count}</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, photo_required_count: Math.min(10, (f.photo_required_count||1)+1) }))}
                      className="h-6 w-6 rounded border flex items-center justify-center text-muted-foreground hover:bg-muted">+</button>
                  </div>
                </div>
              )}
            </div>
            {/* Blockiert andere */}
            <div className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${form.blocks_others ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400' : 'border-amber-200 bg-amber-50/30'}`}>
              <input type="checkbox" checked={form.blocks_others || false} onChange={e => setForm(f => ({ ...f, blocks_others: e.target.checked }))} className="h-5 w-5 rounded mt-0.5 accent-amber-600" data-testid="blocks-others-check" />
              <div>
                <Label className="cursor-pointer text-sm font-semibold text-amber-700 dark:text-amber-400">Blockiert andere Aufgaben bis erledigt</Label>
                <p className="text-xs text-amber-600/80 mt-0.5">Für sicherheitskritische Aufgaben (z.B. Tresorübergabe, Kassenlade).</p>
              </div>
            </div>
            {/* Anhänge: Bild und/oder PDF */}
            <div className="space-y-2 border-t pt-3">
              <Label className="text-sm font-semibold">Anhang <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="grid grid-cols-2 gap-2">
                {/* Bild */}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Referenzbild</p>
                  {imagePreview ? (
                    <div className="relative group">
                      <img src={imagePreview} alt="Vorschau" className="w-full h-24 object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => { setImagePreview(null); setPendingImageFile(null); }}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      ><X className="h-3 w-3" /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => imageFileRef.current?.click()}
                      className="w-full h-16 border-2 border-dashed border-border rounded-lg flex items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-muted/30 transition-colors">
                      <Plus className="h-3.5 w-3.5" /> Bild wählen
                    </button>
                  )}
                  <input ref={imageFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setPendingImageFile(f);
                      setImagePreview(URL.createObjectURL(f));
                    }} />
                </div>
                {/* PDF */}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">PDF-Anleitung</p>
                  {attachmentPdfUrl ? (
                    <div className="h-16 border rounded-lg bg-red-50 dark:bg-red-900/10 flex items-center justify-center gap-1.5 relative group">
                      <FileText className="h-5 w-5 text-red-500" />
                      <span className="text-xs text-red-700 font-medium">PDF vorhanden</span>
                      <button
                        type="button"
                        onClick={() => { setAttachmentPdfUrl(null); setPendingPdfFile(null); }}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      ><X className="h-3 w-3" /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => pdfFileRef.current?.click()}
                      className="w-full h-16 border-2 border-dashed border-border rounded-lg flex items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-muted/30 transition-colors">
                      <Plus className="h-3.5 w-3.5" /> PDF wählen
                    </button>
                  )}
                  <input ref={pdfFileRef} type="file" accept="application/pdf" className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setPendingPdfFile(f);
                      setAttachmentPdfUrl('pending');
                    }} />
                </div>
              </div>
            </div>
            {/* iOS-Debug-Panel (temporär) */}
            {uploadDebug && (
              <div style={{background:'#1e1e1e',color:'#0f0',fontFamily:'monospace',fontSize:'11px',padding:'8px',borderRadius:'8px',whiteSpace:'pre-wrap',maxHeight:'120px',overflowY:'auto'}} data-testid="upload-debug">
                {uploadDebug}
                <button onClick={() => setUploadDebug(null)} style={{display:'block',marginTop:'4px',color:'#888',fontSize:'10px',background:'none',border:'none',cursor:'pointer'}}>schließen</button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Speichern' : 'Anlegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
